import React from 'react';
import { View, Text, Platform, Linking, Pressable } from 'react-native';
import { Icon } from 'react-native-elements';
import moment from 'moment-timezone';
import OrderItemProps from 'types/components/OrderItemProps';
import UserRoleType from 'types/enums/UserRoleType';
import { getOrderColorByStatus } from 'types/enums/OrderStatusType';
import AppContext from 'contexts/AppContext';
import { styles } from 'styles/components/orderItem';
import { useTranslation } from 'react-i18next';

export default ({ order, onPress, isMediumScreen, isParkingCommerce }: OrderItemProps): JSX.Element => {
  const { authData } = React.useContext(AppContext);
  const isCommerceEditor = authData?.user.role === UserRoleType.CommerceEditor;
  const orderDateTime = moment(order.localDateTime).format('DD/MM/YYYY HH:mm:ss');
  const vehicleData =
    isParkingCommerce && order?.delivery ? order?.delivery.split(',').map((data) => data.split(': ')[1]) : null;

  const { t } = useTranslation();

  return (
    <View style={[styles.orderItemContainer]}>
      <Pressable onPress={onPress}>
        <View
          style={[
            isMediumScreen ? styles.cardOrderItemLarge : styles.cardOrderItemSmall,
            isCommerceEditor && { justifyContent: 'center' },
          ]}
        >
          {isMediumScreen ? (
            <>
              <View style={{ flex: 0.5, flexDirection: 'row', justifyContent: 'flex-start' }}>
                <Text style={[styles.textOrderIdLarge, isCommerceEditor && { textAlign: 'center' }]}>{order.id}</Text>
              </View>
              <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'center' }}>
                <Text style={[styles.textOrderDateTimeLarge, isCommerceEditor && { textAlign: 'center' }]}>
                  {orderDateTime}
                </Text>
              </View>
            </>
          ) : (
            <View style={{ flex: 0.5, flexDirection: 'column', justifyContent: 'center' }}>
              <Text style={[styles.textOrderIdSmall, isCommerceEditor && { textAlign: 'center' }]}>{order.id}</Text>
              <Text style={[styles.textOrderDateTimeSmall, isCommerceEditor && { textAlign: 'center' }]}>
                {orderDateTime}
              </Text>
            </View>
          )}
          {!isMediumScreen && isParkingCommerce && (
            <View style={{ flex: 0.5, flexDirection: 'column', justifyContent: 'center' }}>
              <Text style={[styles.textCarModelSmall, isCommerceEditor && { textAlign: 'center' }]}>
                {vehicleData && vehicleData[0]}
              </Text>
              <Text style={[styles.textCarPlateSmall, isCommerceEditor && { textAlign: 'center' }]}>
                {vehicleData && vehicleData[1]}
              </Text>
            </View>
          )}
          {!isParkingCommerce && (
            <View style={{ flex: isMediumScreen ? 1 : 0.2, flexDirection: 'row', justifyContent: 'center' }}>
              <Text
                style={{
                  color: '#FFFFFF',
                  fontSize: 14,
                  fontWeight: '400',
                  lineHeight: 24,
                  textAlign: 'center',
                  alignSelf: 'center',
                }}
              >{`${order.delivery ? 'Delivery' : t('pickup')}`}</Text>
            </View>
          )}
          {isMediumScreen && isParkingCommerce && (
            <>
              <View style={{ flex: isMediumScreen ? 1 : 0.2, flexDirection: 'row', justifyContent: 'center' }}>
                <Text
                  style={{
                    color: '#FFFFFF',
                    fontSize: 14,
                    fontWeight: '400',
                    lineHeight: 24,
                    textAlign: 'center',
                    alignSelf: 'center',
                  }}
                >{`${vehicleData && vehicleData[1] ? vehicleData[1] : ''}`}</Text>
              </View>
              <View style={{ flex: isMediumScreen ? 1 : 0.2, flexDirection: 'row', justifyContent: 'center' }}>
                <Text
                  style={{
                    color: '#FFFFFF',
                    fontSize: 14,
                    fontWeight: '400',
                    lineHeight: 24,
                    textAlign: 'center',
                    alignSelf: 'center',
                  }}
                >{`${vehicleData && vehicleData[0]}`}</Text>
              </View>
            </>
          )}
          <View
            style={{
              flex: isMediumScreen ? 1 : 0.3,
              flexDirection: 'row',
              justifyContent: isMediumScreen ? 'center' : 'space-evenly',
            }}
          >
            <View
              style={{
                width: 15,
                height: 15,
                borderRadius: 15,
                backgroundColor: getOrderColorByStatus(order.status),
                justifyContent: 'center',
                alignSelf: 'center',
              }}
            />
            <Text
              style={{ color: '#FFFFFF', justifyContent: 'center', fontSize: 14, marginLeft: isMediumScreen ? 15 : 10 }}
            >
              ↓
            </Text>
          </View>
          <View
            style={{
              flexDirection: isMediumScreen ? 'row' : 'column',
              flex: isMediumScreen ? 1 : undefined,
              justifyContent: 'center',
            }}
          >
            <Icon
              name="logo-whatsapp"
              type="ionicon"
              color="#FFFFFF"
              size={25}
              containerStyle={isMediumScreen ? styles.whatsappIconLarge : styles.whatsappIconSmall}
              onPress={() => {
                if (Platform.OS === 'web') {
                  window.open(`https://api.whatsapp.com/send?phone=${order.phone}`, '_blank');
                } else {
                  Linking.openURL(`https://api.whatsapp.com/send?phone=${order.phone}`);
                }
              }}
            />
            {isMediumScreen && <Text style={{ color: '#FFFFFF', alignSelf: 'center' }}>{order.phone}</Text>}
          </View>
          {!isParkingCommerce && (
            <View
              style={{
                flexDirection: isMediumScreen ? 'row' : 'column',
                flex: isMediumScreen ? 1 : undefined,
                justifyContent: 'center',
              }}
            >
              <Icon
                name="mail-sharp"
                type="ionicon"
                color={'#FFFFFF'}
                containerStyle={isMediumScreen ? styles.whatsappIconLarge : styles.whatsappIconSmall}
                size={25}
                onPress={() => {
                  const email = order.guestEmail;
                  if (email) {
                    const mailtoLink = `mailto:${email}`;
                    if (Platform.OS === 'web') {
                      window.open(mailtoLink, '_blank');
                    } else {
                      Linking.openURL(mailtoLink);
                    }
                  }
                }}
              />
              {isMediumScreen && <Text style={{ color: '#FFFFFF', alignSelf: 'center' }}>{order.guestEmail}</Text>}
            </View>
          )}
        </View>
      </Pressable>
    </View>
  );
};
